import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { CheckSheetAssignmentProjection, User } from '@completion/models';
import {
  getSelectedSupplierCheckSheetAssignments,
  getSelectedProcurementPackages,
  getVendorUsersForSelectedScope,
  getSelectedVendorUsersForScope,
  getCurrentProject,
} from '@completion/selectors';
import { Store } from '@ngrx/store';
import { first, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ProcurementPackage } from '@completion/models';
import {
  LoadProcurementPackages,
  LoadSuppliers,
  LoadVendors,
  LoadPackageSow,
  LoadLineTagSow,
  ClearAvailableCheckSheetAssignments,
  ClearSelectedProcurementPackages,
  ResetSupplierState,
  SetPreparedTransmittals,
  SetSelectedCheckSheetAssignments,
} from '@completion/actions';
import { UserChecksheetAssignment } from '../shared/models/user-checksheet-assignment';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog';
import { UserAccessService } from '../core/services/user-access.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-supplier',
  templateUrl: './supplier.component.html',
  styleUrls: ['./supplier.component.scss'],
  standalone: false,
})
export class SupplierComponent implements OnInit {
  selectedProcurementPackages: ProcurementPackage[] = [];
  selectedAssignments: CheckSheetAssignmentProjection[] = [];
  selectedUsers: User[] = [];
  selectedVendorUsers: User[] = [];
  procurementPackageForm: FormGroup;
  supplierForm: FormGroup;
  verifyForm: FormGroup;
  sendForm: FormGroup;
  private destroy$ = new Subject<void>();
  completedSuccess = false;
  manualDialogVisible = false;

  constructor(
    private store: Store,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private access: UserAccessService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project) => {
        if (project) {
          this.store.dispatch(new LoadProcurementPackages());
          this.store.dispatch(new LoadSuppliers());
          this.store.dispatch(new LoadVendors());
          this.store.dispatch(new LoadPackageSow());
        }
      });

    if (!this.access.canAccessSupplierModule) {
      this.router.navigate(['/']);
    }

    // Reflect currently selected vendor users into local state AND form control
    this.store
      .select(getSelectedVendorUsersForScope)
      .pipe(takeUntil(this.destroy$))
      .subscribe((vendorUsers) => {
        // Always update, including empty arrays, so validation toggles correctly
        this.selectedVendorUsers = vendorUsers ? [...vendorUsers] : [];
        if (this.supplierForm) {
          const ctrl = this.supplierForm.get('selectedSuppliers');
          ctrl.setValue([...this.selectedVendorUsers]);
          ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
        }
      });

    this.procurementPackageForm = this.fb.group({
      selectedProcurementPackages: [[]],
      selectedAssignments: [[], Validators.required],
    });

    // Require at least one supplier to proceed
    this.supplierForm = this.fb.group({
      selectedSuppliers: [[], [Validators.required, Validators.minLength(1)]],
    });
    // Initialize with currently selected users if any
    {
      const ctrl = this.supplierForm.get('selectedSuppliers');
      ctrl.setValue([...this.selectedVendorUsers]);
      ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    }

    this.verifyForm = this.fb.group({});
    this.sendForm = this.fb.group({});

    this.store
      .select(getSelectedProcurementPackages)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.selectedProcurementPackages = [...data]; // Ensure the array is not frozen
          this.procurementPackageForm.get('selectedProcurementPackages').setValue([...data]);
        }
      });

    // Keep available vendor users for display only (single subscription)
    this.store
      .select(getVendorUsersForSelectedScope)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.selectedUsers = data ? [...data] : [];
      });

    this.store
      .select(getSelectedSupplierCheckSheetAssignments)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.selectedAssignments = [...data]; // Ensure the array is not frozen
          this.procurementPackageForm.get('selectedAssignments').setValue([...data]);
        }
      });
  }

  onComplete(stepper: MatStepper) {
    // open a confirm dialog to confirm moving to final step (sending delayed)
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '600px',
      data: {
        title: 'Complete Supplier Selection',
        message:
          'You will proceed to the final step. The transmittal will be sent automatically after a 10 second grace period unless you cancel.',
        isCancelDisplay: true,
        confirmationTitle: 'Proceed',
        severity: 'info',
        icon: 'send',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe((result) => {
        if (result) {
          // Prepare transmittals from current selections
          const prepared: UserChecksheetAssignment[] = (this.selectedVendorUsers || []).flatMap((user) =>
            (this.selectedAssignments || []).map(
              (assignment) =>
                ({
                  user,
                  checkSheetAssignmentProjection: assignment,
                }) as UserChecksheetAssignment,
            ),
          );
          this.store.dispatch(new SetPreparedTransmittals(prepared));
          // Move to completed step now; actual SendTransmittal happens there after countdown
          stepper.next();
        }
      });
  }

  onCancelSendForStepper(stepper: MatStepper) {
    stepper.previous();
  }

  onTransmittalSuccess() {
    // Hide Back button on Completed step after successful send
    this.completedSuccess = true;
  }

  openManual(): void {
    this.manualDialogVisible = true;
  }

  reset(stepper: MatStepper) {
    this.store.dispatch(new ResetSupplierState());
    this.store.dispatch(new LoadProcurementPackages());
    this.store.dispatch(new LoadSuppliers());
    this.store.dispatch(new LoadVendors());
    this.store.dispatch(new LoadPackageSow());
    stepper.reset();
    this.selectedProcurementPackages = [];
    this.selectedAssignments = [];
    this.selectedUsers = [];
    this.selectedVendorUsers = [];
  }

  get hasSelectedSuppliers(): boolean {
    const arr = (this.supplierForm?.get('selectedSuppliers')?.value as any[]) || [];
    return Array.isArray(arr) && arr.length > 0;
  }

  getStepState(stepper: MatStepper, index: number): 'completed' | 'active' | 'pending' {
    const current = stepper?.selectedIndex ?? 0;
    if (current > index) return 'completed';
    if (current === index) return 'active';
    return 'pending';
  }

  onMainTabChanged(event: MatTabChangeEvent): void {
    if (event.index !== 1) {
      return;
    }
    this.store.dispatch(new SetSelectedCheckSheetAssignments([]));
    this.store.dispatch(new ClearAvailableCheckSheetAssignments());
    this.store.dispatch(new ClearSelectedProcurementPackages());
    this.store.dispatch(new LoadVendors());
    this.store.dispatch(new LoadLineTagSow());
  }
}
